<script>
import {
  fullSchema,
  buildEditor,
  EditorView,
  ArticleMarkdownSerializer,
  ArticleMarkdownTransformer,
  EditorState,
  Selection,
} from '@chatwoot/prosemirror-schema';
import {
  suggestionsPlugin,
  triggerCharacters,
} from '@chatwoot/prosemirror-schema/src/mentions/plugin';
import { insertAtCursor } from 'dashboard/helper/editorHelper';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { useAlert } from 'dashboard/composables';
import { ARTICLE_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';
import { MAXIMUM_FILE_UPLOAD_SIZE } from '../helpers/contants';

import TagAgents from 'next/wgpt/TagAgents.vue';

// Keep in sync with the `w-96` / `max-h-[12.5rem]` mention box in the template.
const MENTION_BOX_WIDTH = 384;
const MENTION_BOX_HEIGHT = 200;
const MENTION_BOX_GAP = 4;

const createState = (
  content,
  placeholder,
  // eslint-disable-next-line default-param-last
  plugins = [],
  // eslint-disable-next-line default-param-last
  methods = {},
  enabledMenuOptions
) => {
  return EditorState.create({
    doc: new ArticleMarkdownTransformer(fullSchema).parse(content),
    plugins: buildEditor({
      schema: fullSchema,
      placeholder,
      methods,
      plugins,
      enabledMenuOptions,
    }),
  });
};

export default {
  components: {
    TagAgents,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    editorId: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    autofocus: {
      type: Boolean,
      default: true,
    },
    uploadFile: {
      type: Function,
      default: () => {},
    },
    agents: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'blur',
    'input',
    'update:modelValue',
    'keyup',
    'focus',
    'keydown',
    'fileUploadError',
    'fileUploadSuccess',
    'fileUploadStart',
  ],
  data() {
    return {
      showUserMentions: false,
      mentionSearchKey: '',
      mentionPosition: null,
      enabledMenuOptions: ARTICLE_EDITOR_MENU_OPTIONS.filter(
        option => option !== 'insertTable'
      ),
      isTextSelected: false, // Tracks text selection and prevents unnecessary re-renders on mouse selection
    };
  },
  watch: {
    modelValue(newValue = '') {
      if (newValue !== this.contentFromEditor()) {
        this.reloadState();
      }
    },
    editorId() {
      this.reloadState();
    },
    showUserMentions(isOpen) {
      // Capture so scrolls inside the card dialog are seen too, not just the window.
      const toggle = isOpen ? 'addEventListener' : 'removeEventListener';
      window[toggle]('scroll', this.updateMentionPosition, true);
      window[toggle]('resize', this.updateMentionPosition);
    },
  },

  created() {
    // The editor view, its state, the suggestion range and the ProseMirror plugins are
    // kept off `data` on purpose, Vue would proxy them and break ProseMirror internals.
    this.editorView = null;
    this.range = null;
    this.plugins = [
      suggestionsPlugin({
        matcher: triggerCharacters('@', 0),
        suggestionClass: '',
        onEnter: args => {
          this.range = args.range;
          this.mentionSearchKey = args.text || '';
          this.showUserMentions = true;
          this.updateMentionPosition();
          return false;
        },
        onChange: args => {
          this.range = args.range;
          this.mentionSearchKey = args.text;
          this.updateMentionPosition();
          return false;
        },
        onExit: () => {
          this.range = null;
          this.mentionSearchKey = '';
          this.showUserMentions = false;
          return false;
        },
        // The mention list has no focusable input of its own, so the editor drives its
        // keyboard navigation. Returning true keeps ProseMirror from also handling the key.
        onKeyDown: ({ event }) =>
          this.$refs.tagAgents?.handleKeyDown(event) ?? false,
      }),
    ];
    this.editorState = createState(
      this.modelValue,
      this.placeholder,
      this.plugins,
      { onImageUpload: this.openFileBrowser },
      this.enabledMenuOptions
    );
  },
  mounted() {
    this.createEditorView();

    if (this.autofocus) {
      this.focusEditorInputField();
    }
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.updateMentionPosition, true);
    window.removeEventListener('resize', this.updateMentionPosition);
    this.editorView?.destroy();
    this.editorView = null;
  },
  methods: {
    contentFromEditor() {
      if (this.editorView) {
        return ArticleMarkdownSerializer.serialize(this.editorView.state.doc);
      }
      return '';
    },
    openFileBrowser() {
      this.$refs.imageUploadInput.click();
    },
    onFileChange() {
      const file = this.$refs.imageUploadInput.files[0];

      if (checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
        this.uploadImageToStorage(file);
      } else {
        useAlert(
          this.$t(
            'WGPT_BOARDS.ITEM.LIST.CARD.EDIT.ATTACHMENTS.API.FILE_SIZE_ERROR',
            {
              size: MAXIMUM_FILE_UPLOAD_SIZE,
            }
          )
        );
      }

      this.$refs.imageUploadInput.value = '';
    },
    async uploadImageToStorage(file) {
      this.$emit('fileUploadStart');
      try {
        const fileUrl = await this.uploadFile(file);

        if (fileUrl) {
          this.onImageUploadStart(fileUrl);
        }

        this.$emit('fileUploadSuccess');
      } catch (error) {
        this.$emit('fileUploadError', error);
        useAlert(
          this.$t(
            'WGPT_BOARDS.ITEM.LIST.CARD.EDIT.ATTACHMENTS.API.UPLOAD_ERROR'
          )
        );
      }
    },
    onImageUploadStart(fileUrl) {
      const { selection } = this.editorView.state;
      const from = selection.from;
      const node = this.editorView.state.schema.nodes.image.create({
        src: fileUrl,
      });
      const paragraphNode = this.editorView.state.schema.node('paragraph');
      if (node) {
        // Insert the image and the caption wrapped inside a paragraph
        const tr = this.editorView.state.tr
          .replaceSelectionWith(paragraphNode)
          .insert(from + 1, node);

        this.editorView.dispatch(tr.scrollIntoView());
        this.focusEditorInputField();
      }
    },
    reloadState() {
      this.range = null;
      this.mentionSearchKey = '';
      this.showUserMentions = false;
      this.editorState = createState(
        this.modelValue,
        this.placeholder,
        this.plugins,
        { onImageUpload: this.openFileBrowser },
        this.enabledMenuOptions
      );
      this.editorView.updateState(this.editorState);
      this.focusEditorInputField();
    },
    createEditorView() {
      this.editorView = new EditorView(this.$refs.editor, {
        state: this.editorState,
        dispatchTransaction: tx => {
          this.editorState = this.editorState.apply(tx);
          this.editorView.updateState(this.editorState);
          if (tx.docChanged) {
            this.emitOnChange();
          }
          this.checkSelection(this.editorState);
        },
        handleDOMEvents: {
          keyup: this.onKeyup,
          focus: this.onFocus,
          blur: this.onBlur,
          keydown: this.onKeydown,
          paste: (view, event) => {
            const data = event.clipboardData.files;
            if (data.length > 0) {
              data.forEach(file => {
                // Check if the file is an image
                if (file.type.includes('image')) {
                  this.uploadImageToStorage(file);
                }
              });
              event.preventDefault();
            }
          },
        },
      });
    },
    handleKeyEvents() {},
    focusEditorInputField() {
      const { tr } = this.editorView.state;
      const selection = Selection.atEnd(tr.doc);

      this.editorView.dispatch(tr.setSelection(selection));
      this.editorView.focus();
    },
    emitOnChange() {
      const content = this.contentFromEditor();
      this.$emit('update:modelValue', content);
      this.$emit('input', content);
    },
    onKeyup() {
      this.$emit('keyup');
    },
    onKeydown() {
      this.$emit('keydown');
    },
    onBlur() {
      this.$emit('blur');
    },
    onFocus() {
      this.$emit('focus');
    },
    checkSelection(editorState) {
      const { from, to } = editorState.selection;
      // Check if there's a selection (from and to are different)
      const hasSelection = from !== to;
      // If the selection state is the same as the previous state, do nothing
      if (hasSelection === this.isTextSelected) return;
      // Update the selection state
      this.isTextSelected = hasSelection;

      const { editorRoot } = this.$refs;

      // Toggle the 'has-selection' class based on whether there's a selection
      editorRoot.classList.toggle('has-selection', hasSelection);
      // If there's a selection, update the menubar position
      if (hasSelection) this.setMenubarPosition(editorState);
    },
    setMenubarPosition(editorState) {
      if (!editorState.selection) return;

      // Get the start and end positions of the selection
      const { from, to } = editorState.selection;
      const { editorRoot } = this.$refs;
      // Get the editor's position relative to the viewport
      const { left: editorLeft, top: editorTop } =
        editorRoot.getBoundingClientRect();

      // Get the editor's width
      const editorWidth = editorRoot.offsetWidth;
      const menubarWidth = 480; // Menubar width (adjust as needed (px))

      // Get the end position of the selection
      const { bottom: endBottom, right: endRight } =
        this.editorView.coordsAtPos(to);
      // Get the start position of the selection
      const { left: startLeft } = this.editorView.coordsAtPos(from);

      // Calculate the top position for the menubar (10px below the selection)
      const top = endBottom - editorTop + 10;
      // Calculate the left position for the menubar
      // This centers the menubar on the selection while keeping it within the editor's bounds
      const left = Math.max(
        0,
        Math.min(
          (startLeft + endRight) / 2 - editorLeft,
          editorWidth - menubarWidth
        )
      );
      // Set the CSS custom properties for positioning the menubar
      editorRoot.style.setProperty('--selection-top', `${top}px`);
      editorRoot.style.setProperty('--selection-left', `${left}px`);
    },
    // The mention box is fixed so it can escape the card dialog's scroll containers.
    // `fixed` resolves against the nearest ancestor that establishes a containing block
    // though, and the dialog does establish one through its backdrop-filter, so the
    // caret's viewport coordinates have to be re-expressed inside that box. The probe
    // element spans it, which is the only reliable way to measure it.
    updateMentionPosition() {
      const { fixedOrigin } = this.$refs;
      if (!this.editorView || !this.range || !fixedOrigin) {
        this.mentionPosition = null;
        return;
      }

      const from = Math.min(
        this.range.from,
        this.editorView.state.doc.content.size
      );
      const caret = this.editorView.coordsAtPos(from);
      const box = fixedOrigin.getBoundingClientRect();
      const left = Math.max(
        0,
        Math.min(caret.left, window.innerWidth - MENTION_BOX_WIDTH)
      );
      const opensUpward =
        window.innerHeight - caret.bottom <
        MENTION_BOX_HEIGHT + MENTION_BOX_GAP;

      this.mentionPosition = {
        left: `${left - box.left}px`,
        ...(opensUpward
          ? { bottom: `${box.bottom - caret.top + MENTION_BOX_GAP}px` }
          : { top: `${caret.bottom + MENTION_BOX_GAP - box.top}px` }),
      };
    },
    insertSpecialContent(type, content) {
      if (type !== 'mention' || !this.editorView || !this.range) return;

      // The article schema has no mention node, so a mention is stored as a link that
      // serializes back to `[@name](mention://user/:id/:name)`.
      const { from, to } = this.range;
      const { schema } = this.editorView.state;
      const href = `mention://user/${content.id}/${encodeURIComponent(content.name)}`;
      const node = schema.text(`@${content.name}`, [
        schema.marks.link.create({ href }),
      ]);

      this.editorState = insertAtCursor(this.editorView, node, from, to);
      this.range = null;
      this.mentionSearchKey = '';
      this.showUserMentions = false;
      this.emitOnChange();
    },
  },
};
</script>

<template>
  <div
    dir="auto"
    class="border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6 disabled:border-n-weak dark:disabled:border-n-weak block w-full reset-base text-sm border !rounded-lg focus:border-n-brand dark:focus:border-n-brand bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-11 dark:placeholder:text-n-slate-11 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
    @keydown.enter.stop
  >
    <div ref="editorRoot" class="editor-root wgpt-boards-editor">
      <input
        ref="imageUploadInput"
        type="file"
        accept="image/png, image/jpeg, image/jpg, image/gif, image/webp"
        hidden
        @change="onFileChange"
      />
      <div
        ref="fixedOrigin"
        aria-hidden="true"
        class="fixed inset-0 invisible pointer-events-none"
      />
      <TagAgents
        v-if="showUserMentions"
        ref="tagAgents"
        class="fixed z-50 w-96"
        :style="mentionPosition"
        :agents="agents"
        :search-key="mentionSearchKey"
        @select-agent="content => insertSpecialContent('mention', content)"
      />
      <div ref="editor" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.editor-root {
  @apply relative;
}

.wgpt-boards-editor ::v-deep {
  .ProseMirror {
    @apply px-3 pt-10 pb-2 rounded-lg break-words outline-none;
    outline-color: #fd6262;
    outline-offset: -2px;
  }

  .ProseMirror-menubar {
    @apply absolute m-0 start-0 top-1.5 p-2 rounded-md bg-transparent;
  }

  .ProseMirror-menuitem {
    @apply h-5 !important;
  }

  .ProseMirror-menu-active {
    @apply bg-n-alpha-2;
  }

  .ProseMirror-icon {
    @apply p-1 w-3 h-3 text-n-slate-12 dark:text-n-slate-12 !important;
  }

  .ProseMirror-woot-style {
    min-height: 8rem;
    max-height: unset;
    overflow: auto;
  }
}
</style>
